// src/components/MapViewer.js
import React, { useState } from 'react';

// On ne peut pas importer MapPicker ici si MapPicker importe MapViewer
// Utilisons un import dynamique ou restructurons

const MapViewer = ({ 
  show, 
  onHide, 
  latitude, 
  longitude, 
  siteName = "Position",
  siteAddress = "",
  siteStatus = 1
}) => {
  const [MapPickerComponent, setMapPickerComponent] = useState(null);
  const [key, setKey] = useState(Date.now());

  // Charger MapPicker dynamiquement pour éviter les dépendances circulaires
  React.useEffect(() => {
    if (show && !MapPickerComponent) {
      import('./MapPicker').then(module => {
        setMapPickerComponent(() => module.default);
      });
    }
  }, [show, MapPickerComponent]);

  const handleClose = () => {
    onHide();
    // Forcer un re-render pour la prochaine ouverture
    setTimeout(() => setKey(Date.now()), 100);
  };

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545'
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop fade show"></div>
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        tabIndex="-1"
        style={{ display: 'block' }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: '12px' }}>
            {/* Header avec meilleur padding */}
            <div className="modal-header" style={{ 
              backgroundColor: COLORS.lightBg,
              borderBottom: `1px solid ${COLORS.lightBg}`,
              borderRadius: '12px 12px 0 0',
              padding: '20px 24px' // Padding augmenté
            }}>
              <div className="d-flex align-items-center">
                <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                  width: '40px',
                  height: '40px',
                  backgroundColor: COLORS.primary,
                  color: COLORS.white
                }}>
                  <i className="bi bi-map" style={{ fontSize: '1.2rem' }}></i>
                </div>
                <div>
                  <h5 className="modal-title mb-0" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontSize: '1.25rem'
                  }}>
                    {siteName}
                  </h5>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-geo-alt me-1"></i>
                    Visualisation sur carte
                  </p>
                </div>
              </div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={handleClose}
                style={{ 
                  fontSize: '0.875rem',
                  padding: '12px',
                  margin: '-12px -12px -12px auto'
                }}
              ></button>
            </div>

            {/* Body avec padding amélioré */}
            <div className="modal-body" style={{ padding: '0' }}>
              <div style={{ height: '500px' }}>
                {MapPickerComponent ? (
                  <MapPickerComponent
                    key={key}
                    onLocationSelect={() => {}} 
                    initialPosition={[
                      parseFloat(latitude) || 13.5137,
                      parseFloat(longitude) || 2.1098
                    ]}
                    initialZoom={5}
                    latitude={latitude}
                    longitude={longitude}
                    showCoordinatesInputs={false}
                    showNigerRegions={false}
                    isViewOnly={true}
                    selectedSiteName={siteName}
                    selectedSiteAddress={siteAddress}
                    showInstructions={false}
                  />
                ) : (
                  <div className="d-flex flex-column justify-content-center align-items-center h-100" style={{ 
                    backgroundColor: COLORS.lightBg,
                    padding: '40px'
                  }}>
                    <div className="spinner-border" style={{ 
                      color: COLORS.primary,
                      width: '3rem',
                      height: '3rem',
                      marginBottom: '20px'
                    }} role="status">
                      <span className="visually-hidden">Chargement de la carte...</span>
                    </div>
                    <p style={{ 
                      color: COLORS.darkText,
                      fontSize: '1rem',
                      marginBottom: '0'
                    }}>
                      Chargement de la carte...
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer avec padding amélioré */}
            <div className="modal-footer" style={{ 
              borderTop: `1px solid ${COLORS.lightBg}`,
              padding: '16px 24px', // Padding augmenté
              backgroundColor: COLORS.lightBg
            }}>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2" style={{ color: COLORS.secondary }}></i>
                  <div>
                    <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                      <strong>Coordonnées :</strong> {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
                    </p>
                    {siteAddress && (
                      <p className="mb-0 text-muted small" style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                        <i className="bi bi-geo me-1"></i>
                        {siteAddress.length > 60 ? siteAddress.substring(0, 60) + '...' : siteAddress}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <button
                    className="btn fw-semibold"
                    onClick={() => {
                      navigator.clipboard.writeText(`${latitude}, ${longitude}`);
                      alert('Coordonnées copiées dans le presse-papier !');
                    }}
                    style={{
                      color: COLORS.secondary,
                      borderColor: COLORS.secondary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.875rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.color = COLORS.white;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.secondary;
                    }}
                  >
                    <i className="bi bi-clipboard me-1"></i>
                    Copier coordonnées
                  </button>
                  
                  <button
                    className="btn fw-semibold"
                    onClick={handleClose}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: COLORS.white,
                      borderRadius: '8px',
                      padding: '8px 20px',
                      fontSize: '0.875rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.lightBg;
                      e.target.style.borderColor = COLORS.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.white;
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles CSS pour le modal */}
      <style jsx="true">{`
        .modal-backdrop {
          z-index: 1040;
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .modal {
          z-index: 1050;
        }
        
        .modal.show {
          display: block;
          background-color: rgba(0, 0, 0, 0.3);
        }
        
        .modal-content {
          border: none;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }
        
        .btn-close {
          background-size: 0.75em;
          opacity: 0.7;
          transition: all 0.2s ease;
        }
        
        .btn-close:hover {
          opacity: 1;
          transform: rotate(90deg);
        }
        
        /* Responsive pour mobile */
        @media (max-width: 768px) {
          .modal-dialog {
            margin: 10px;
          }
          
          .modal-header {
            padding: 16px 20px !important;
          }
          
          .modal-footer {
            padding: 16px 20px !important;
          }
          
          .modal-footer .d-flex {
            flex-direction: column;
            gap: 12px;
          }
          
          .modal-footer .d-flex > div {
            width: 100%;
            text-align: center;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </>
  );
};

export default MapViewer;